import Icon, { ICONS } from "@/components/Core/Icons";
import { CalculatedDiff, useActivityDiffs } from "@/hooks/useActivityDiffs";
import { useAssetComponents } from "@/hooks/useAssetComponents";
import { useSaveFile } from "@/hooks/useSaveFile";
import { cn } from "@/lib/utils";
import {
  ChatContextCodeSnippet,
  useAiContextStore,
  useEngSpecOpenedFile,
} from "@/store/zustand/aiContextStore";
import useDesignSystemStore from "@/store/zustand/aiDesignSystemBuilder";
import { useCanvasStore } from "@/store/zustand/canvasStore";
import {
  diffEditorForFile,
  EditorContext,
  EditorFile,
  FILE_DOES_NOT_EXIST,
  filesEqual,
  useCodebaseStore,
} from "@/store/zustand/codebaseStore";
import useLayoutStore, {
  LayoutMode,
  LeftTab,
} from "@/store/zustand/layoutStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { track } from "@/utils/events/tracker";
import FileIcons from "@/utils/seti-ui-icons.json";
import Editor, { DiffEditor } from "@monaco-editor/react";
import { Cross2Icon } from "@radix-ui/react-icons";
import axios from "axios";
import _ from "lodash";
import { EyeIcon, History, InfoIcon, PencilIcon, X } from "lucide-react";
import { editor } from "monaco-editor";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";
import { toast } from "sonner";
import {
  getEditorLanguageForFilename,
  useActiveMainCanvas,
  useCanvasId,
  useDeepMemo,
  usePrevious,
} from "@/utils/utils";
import { useSupabase } from "../contexts/DatabaseSyncWrapper";
import { Button } from "../ui/button";
import { initEditor } from "./monacoExtras";
import { useSession } from "@clerk/clerk-react";
import { notifyJumpToLine } from "@/utils/localEditingUtils";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useDesignCommandManager } from "@/store/zustand/designCommandManagerStore";
import useActivityStore from "@/store/zustand/activityStore";

type MonacoType = typeof import("monaco-editor/esm/vs/editor/editor.api");

// ===================================== REFACTOR =====================================
// This can probably be baked into the model

enum FileType {
  Text, // The current file contents from the API
  Diff, // Specifically the git diff -> retrieves the before and after from the API
  CalculatedDiff, // Both the previous and new values are explicit
  SuggestedDiff, // The old values are the current file contents, only the new values are explicit
  Contents, // A file with given manual contents
}

interface Props {}

/**
 * Returns the key for a file, if diff is true it is to be used to get the values before the file has changed
 * If it has a key suffix, it is a custom file and should be treated as such
 */
const getKeyForFile = (file: EditorFile) => {
  if (file.keySuffix) {
    return `${file.filename}-${file.keySuffix}`;
  }

  if (file.diffType) {
    return `before-changes:${file.filename}`;
  }

  if (file.suggestedDiff !== null && file.suggestedDiff !== undefined) {
    return `suggested-changes:${file.filename}`;
  }

  if (file.calculatedDiff) {
    return `calculated-changes:${file.filename}`;
  }

  if (file.rawContents !== undefined && file.rawContents !== null) {
    return `raw-contents:${file.filename}`;
  }

  return file?.filename;
};

const hasUnsavedChangesForFile = (
  currentValueInModel: string,
  currentFileContents: string
) => {
  if (currentValueInModel === undefined || currentValueInModel === null) {
    return false;
  }

  // The file contents have not yet been loaded from the backend
  if (currentFileContents === undefined || currentFileContents === null) {
    return false;
  }

  if (currentFileContents === FILE_DOES_NOT_EXIST) {
    return Boolean(currentValueInModel);
  }

  return currentValueInModel !== currentFileContents;
};

const getShortenedFilename = (filename: string) => {
  if (!filename) {
    return null;
  }
  return filename.split("/").slice(-1)[0];
};

const throttledPushEditOperation = _.throttle(
  (model: editor.ITextModel, newContents: string) => {
    try {
      const range = model?.getFullModelRange();
      model.pushEditOperations(
        [],
        [
          {
            range: range,
            text: newContents,
            forceMoveMarkers: true,
          },
        ],
        () => null
      );
    } catch (e) {
      console.error("Error pushing edit operation", e);
    }
  },
  100
);

interface TabProps {
  file: EditorFile;
  onTabClick: (file: EditorFile) => void;
  onTabDoubleClick: (file: EditorFile) => void;
  onTabCloseClick: (file: EditorFile) => void;
  onTabSave: (file: EditorFile) => void;
  showSave: boolean;
  selected: boolean;
  selectedFile: EditorFile;
}

const Tab: React.FC<TabProps> = React.memo((props) => {
  const {
    file,
    onTabClick,
    onTabDoubleClick,
    onTabCloseClick,
    onTabSave,
    showSave,
    selected,
    selectedFile,
  } = props;

  const isDiff = useMemo(() => {
    return diffEditorForFile(file);
  }, [file]);

  const [isHovered, setIsHovered] = useState(false);

  const shortenedFile = getShortenedFilename(file.filename);
  // @ts-expect-error TS2538
  const iconForFile = FileIcons[shortenedFile];
  // @ts-expect-error TS2538
  const iconForExt = FileIcons[file.filename.split(".").pop()];

  const iconToUse =
    iconForFile ||
    iconForExt ||
    "/icons/file-extension-items/seti-ui/default.svg";

  // TODO: This styling is a bit of a mess, clean it up
  return (
    <div
      className={cn(
        "flex shrink-0 justify-center flex-0 whitespace-nowrap items-center border-r px-2 pr-1.5 h-[calc(100%+2px)] border-b font-normal text-xs text-white cursor-pointer gap-x-1 select-none relative",
        selected
          ? "bg-secondary-100 border-b-secondary-100 before:content-[''] before:absolute before:left-0 before:right-0 before:top-[1px] before:h-[1px] before:bg-primary-0"
          : "bg-secondary-200 hover:bg-secondary-100"
      )}
      style={{
        fontStyle: !file.hardOpened ? "italic" : "normal",
      }}
      onClick={() => {
        onTabClick(file);
      }}
      onDoubleClick={() => {
        onTabDoubleClick(file);
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {file.context === EditorContext.History && (
        <History className="w-3 h-3" />
      )}
      {file.context === EditorContext.EngSpec && (
        <Icon icon={ICONS.AI_LOGO} className="w-4 h-4" />
      )}
      {file.context === EditorContext.Code && (
        <img src={iconToUse} alt={file.filename} className="w-5 h-5" />
      )}
      {getShortenedFilename(file.filename) + (isDiff ? "  (diff)" : "")}
      {showSave && (
        <div
          className="flex text-xs px-1 rounded-sm pointer-events-auto bg-secondary-400 hover:bg-secondary-500 text-white cursor-pointer"
          onClick={(e) => {
            onTabSave(file);

            e.preventDefault();
            e.stopPropagation();
          }}
        >
          Save
        </div>
      )}
      <div
        className={`font-bold text-white p-1 cursor-pointer hover:bg-secondary-400 rounded-sm ${
          isHovered || filesEqual(selectedFile, file)
            ? "opacity-100"
            : "opacity-0"
        }`}
        onClick={(e) => {
          onTabCloseClick(file);

          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Cross2Icon className="w-3 h-3" />
      </div>
    </div>
  );
});

const CodePanel: React.FC<Props> = React.memo((props) => {
  const { session } = useSession();
  const dispatch = useDispatch<any>();

  const attemptJumpToDefinition = useCodebaseStore(
    (state) => state.attemptJumpToDefinition
  );
  const revealLocationAt = useCodebaseStore((state) => state.revealLocationAt);
  const revealLineNumber = useCodebaseStore((state) => state.revealLineNumber);
  const editorState = useCodebaseStore((state) => state.editor);
  const editorContext = useCodebaseStore((state) => state.editorContext);
  const fileToContentsFromApi = useCodebaseStore(
    (state) => state.fileToContents
  );
  const closeFile = useCodebaseStore((state) => state.closeFile);
  const updateFile = useCodebaseStore((state) => state.updateFile);
  const revealLocation = useCodebaseStore((state) => state.revealLocation);
  const openFile = useCodebaseStore((state) => state.openFile);
  const hardOpenFile = useCodebaseStore((state) => state.hardOpenFile);
  const setSelectedFile = useCodebaseStore((state) => state.setSelectedFile);
  const clearHighlightLinesForFile = useCodebaseStore(
    (state) => state.clearHighlightLinesForFile
  );

  const fileToContentsFromApiRef = useRef(fileToContentsFromApi);
  useEffect(() => {
    fileToContentsFromApiRef.current = fileToContentsFromApi;
  }, [fileToContentsFromApi]);

  const canvasId = useCanvasId();

  // Project from DB
  const mainCanvas = useActiveMainCanvas();

  const baseFilePathToProject = useSelector(
    (state: any) => state.codebase.basePathToProject
  );

  const [fileToMonacoModel, setFileToMonacoModel] = useState<{
    [fileKey: string]: editor.IEditorModel;
  }>({});

  const [fileToHasUnsavedChanges, setFileToHasUnsavedChanges] = useState<{
    [fileKey: string]: boolean;
  }>({});
  const [fileToViewState, setFileToViewState] = useState<{
    [fileKey: string]:
      | editor.ICodeEditorViewState
      | editor.IDiffEditorViewState;
  }>({});

  const editorWrapperRef = useRef<any>(null);

  // Whena file changes (e.g. from the API, briefly flash the changed lines)
  const [decorationsHighlightChanges, setDecorationsHighlightChanges] =
    React.useState<any[]>([]);

  const [highlightLineDecoration, setHighlightLineDecoration] = useState(null);

  const monacoObjects = useRef<{
    editor: editor.IStandaloneCodeEditor;
    monaco: MonacoType;
  }>(null);
  const monacoDiffObjects = useRef<{
    editor: editor.IStandaloneDiffEditor;
    monaco: MonacoType;
  }>(null);

  const selectedFileRef = useRef<any>({});

  const editorFiles = editorState[editorContext].editorFiles;
  const selectedFile = editorState[editorContext].selectedFile;

  const selectedFileIsDiff = useMemo(() => {
    return diffEditorForFile(selectedFile);
  }, [selectedFile]);

  const selectedFileKey = selectedFile ? getKeyForFile(selectedFile) : null;

  const setCanvasComponentKey = useCanvasStore(
    (store) => store.setCanvasComponentKey
  );

  const { componentKeyToComponent } = useAssetComponents();
  const filenameToComponent = useMemo(() => {
    const _filenameToComponent = {};
    Object.keys(componentKeyToComponent).forEach((componentKey: string) => {
      const component = componentKeyToComponent[componentKey];

      _filenameToComponent[component.filename] = {
        ...component,
        key: componentKey,
      };

      // Also include the shorter path
      _filenameToComponent[component.filename.split(canvasId + "/")[1]] = {
        ...component,
        key: componentKey,
      };
    });
    return _filenameToComponent;
  }, [componentKeyToComponent]);

  useEffect(() => {
    selectedFileRef.current = selectedFile;
  }, [selectedFile]);

  // Layout status
  const leftTab = useLayoutStore((state) => state.leftTab);
  const leftTabRef = useRef(leftTab);
  useEffect(() => {
    leftTabRef.current = leftTab;
  }, [leftTab]);
  const setNewLayout = useLayoutStore((store) => store.setNewLayout);

  const showAiCodeChange = useLayoutStore((state) => state.showAiCodeChange);
  const setShowAiCodeChange = useLayoutStore(
    (store) => store.setShowAiCodeChange
  );

  const engSpecMode = leftTab === LeftTab.EngSpec && showAiCodeChange;
  const [openedReadme, setOpenedReadme] = React.useState(false);
  const [editorLoaded, setEditorLoaded] = React.useState(false);
  const [diffEditorLoaded, setDiffEditorLoaded] = React.useState(false);

  const { calculatedDiffs } = useActivityDiffs(
    // @ts-expect-error TS2345
    selectedFile?.appliedDiffActivityId
  );

  const { file: engSpecOpenedFile, commit: engSpecOpenedCommit } =
    useEngSpecOpenedFile();
  const setEngSpecOpenedFile = useAiContextStore(
    (state) => state.setEngSpecOpenedFile
  );
  const closeCodeDiffPanel = useAiContextStore(
    (state) => state.closeCodeDiffPanel
  );

  const openedCommitFileRef = useRef(engSpecOpenedFile);
  const selectedCommitRef = useRef(engSpecOpenedCommit);

  useEffect(() => {
    openedCommitFileRef.current = engSpecOpenedFile;
  }, [engSpecOpenedFile]);

  useEffect(() => {
    selectedCommitRef.current = engSpecOpenedCommit;
  }, [engSpecOpenedCommit]);

  const activityStreamList = useActivityStore(
    (state) => state.activityStreamList
  );

  const latestActivity = useMemo(() => {
    return activityStreamList?.find?.((activity) => !activity.change_undone);
  }, [activityStreamList]);

  const nextActivityToRedo = useMemo(() => {
    return [...activityStreamList]
      ?.reverse()
      ?.find?.((activity) => activity.change_undone);
  }, [activityStreamList]);

  const undoChange = useDesignCommandManager((state) => state.undo);
  const redoChange = useDesignCommandManager((state) => state.redo);

  const saveFile = useSaveFile();

  /**
   * Open the README.md file if no files are open
   */
  useEffect(() => {
    // Only auto-open once
    if (openedReadme) {
      return;
    }

    if (editorFiles.length === 0 && baseFilePathToProject) {
      openFile({
        file: {
          filename: baseFilePathToProject + "README.md",
          context: EditorContext.Code,
        },
      });
      setOpenedReadme(true);
    }
  }, [baseFilePathToProject]);

  /**
   * If the user edits a file creating unsaved changes, hard open the file
   */
  useEffect(() => {
    if (
      selectedFile &&
      fileToHasUnsavedChanges[getKeyForFile(selectedFile)] &&
      !selectedFile.hardOpened
    ) {
      hardOpenFile(selectedFile);
    }
  }, [fileToHasUnsavedChanges, selectedFileKey]);

  /**
   * Trigger jump to line/reveal location in editor
   */
  useEffect(() => {
    if (
      (revealLocationAt === null && revealLineNumber === null) ||
      !selectedFile?.filename
    ) {
      return;
    }

    let editor = monacoObjects?.current?.editor;

    if (!editor) {
      return;
    }

    if (revealLocationAt) {
      const position = editor.getModel()?.getPositionAt?.(revealLocationAt);
      if (position) {
        editor.setPosition?.(position);
      }
    } else {
      editor.revealLineInCenter(revealLineNumber);
    }

    if (attemptJumpToDefinition) {
      editor.getAction("editor.action.revealDefinition")?.run?.();
    }

    // Reset values
    revealLocation({
      revealLocationAt: null,
      revealLineNumber: null,
      attemptJumpToDefinition: false,
    });
  }, [revealLocationAt, attemptJumpToDefinition, revealLineNumber]);

  /**
   * Handle if there are no editor files
   *
   * If there is no selected file, select the first one
   */
  useEffect(() => {
    if (editorFiles.length === 0) {
      setSelectedFile(null);

      if (showAiCodeChange) {
        setShowAiCodeChange(false);
      }

      return;
    }

    if (!selectedFile) {
      setSelectedFile(editorFiles[0]);
    }
  }, [editorFiles, selectedFileKey]);

  const prevOpenedFile: any = usePrevious(selectedFile);

  const attachChangesListener = (
    model: editor.ITextModel,
    file: EditorFile,
    fileType: FileType
  ) => {
    if (!model) {
      return;
    }

    model.onDidChangeContent((_) => {
      let newContents;
      try {
        newContents = model.getValue();
      } catch (e) {
        console.error("Error getting new contents", e);
        return;
      }

      const currentContentsFromApi =
        fileToContentsFromApiRef.current?.[file.filename];

      if (fileType === FileType.SuggestedDiff) {
        updateFile(file, {
          suggestedDiff: newContents,
        });
        return;
      }

      setFileToHasUnsavedChanges((prev) => {
        return {
          ...prev,
          [getKeyForFile(file)]: hasUnsavedChangesForFile(
            newContents,
            currentContentsFromApi
          ),
        };
      });
    });
  };

  /**
   * This use effect handles creating and updating models when new files are opened
   * and the contents for those files are fetched from the backend.
   *
   * It also updates the models with new values from the backend only if there are no unsaved changes
   */
  useEffect(() => {
    const updateOrCreateModelForFile = (file: EditorFile) => {
      // @ts-expect-error TS2322
      let fileType: FileType = null,
        // @ts-expect-error TS2322
        calculatedDiff: CalculatedDiff = null;

      if (file.rawContents !== undefined && file.rawContents !== null) {
        fileType = FileType.Contents;
      } else if (
        file.suggestedDiff !== undefined &&
        file.suggestedDiff !== null
      ) {
        fileType = FileType.SuggestedDiff;
      } else if (file.calculatedDiff) {
        fileType = FileType.CalculatedDiff;
        calculatedDiff = file.calculatedDiff;
      } else {
        fileType = file.diffType ? FileType.Diff : FileType.Text;
      }

      if (file.appliedDiffActivityId) {
        const diffForFile = calculatedDiffs.find(
          (diff) => diff.filePath?.split(canvasId + "/")[1] === file?.filename
        );

        if (diffForFile) {
          fileType = FileType.CalculatedDiff;
          calculatedDiff = diffForFile;
        }
      }

      // =====================================================================================

      const fileKey = getKeyForFile(file);
      let monacoModelForFile = fileToMonacoModel[fileKey];

      // Early return for raw contents -> handles its own editing

      if (fileType === FileType.Contents) {
        if (!monacoObjects.current?.monaco) {
          return;
        }

        if (!file.rawContents) {
          throw new Error("Must have raw contents for this file type");
        }

        if (!monacoModelForFile) {
          // Model has not been created yet, create it
          monacoModelForFile = monacoObjects.current.monaco.editor.createModel(
            file.rawContents,
            getEditorLanguageForFilename(file?.filename)
          );

          // Track unsaved changes on the model itself
          attachChangesListener(monacoModelForFile, file, fileType);

          if (monacoModelForFile) {
            setFileToMonacoModel((prev) => ({
              ...prev,
              [fileKey]: monacoModelForFile,
            }));
          }
        } else {
          const prevValue = (
            monacoModelForFile as editor.ITextModel
          )?.getValue();

          const monacoModel = monacoModelForFile as editor.ITextModel;

          if (prevValue !== file.rawContents) {
            if (file.rawContents?.startsWith(prevValue || "")) {
              const textAdded = file.rawContents.slice(prevValue?.length || 0);

              const currentFullRange = monacoModel?.getFullModelRange();
              const insertRange = new monacoObjects.current.monaco.Range(
                currentFullRange.endLineNumber,
                currentFullRange.endColumn,
                currentFullRange.endLineNumber,
                currentFullRange.endColumn
              );

              monacoModel.pushEditOperations(
                [],
                [
                  {
                    range: insertRange,
                    text: textAdded,
                    forceMoveMarkers: true,
                  },
                ],
                () => null
              );
            } else {
              throttledPushEditOperation(monacoModel, file.rawContents);
            }
          }
        }
        return;
      }

      // Early return for activity -> handles its own updating
      if (fileType === FileType.CalculatedDiff) {
        if (!monacoDiffObjects.current?.monaco) {
          return;
        }

        if (!calculatedDiff) {
          throw new Error("Must have calculated diff for this file type");
        }

        if (!monacoModelForFile) {
          if (!monacoDiffObjects.current) {
            return;
          }

          // Model has not been created yet, create it
          const originalModel =
            monacoDiffObjects.current.monaco.editor.createModel(
              calculatedDiff.old === FILE_DOES_NOT_EXIST
                ? ""
                : calculatedDiff.old,
              getEditorLanguageForFilename(file?.filename)
            );
          const newModel = monacoDiffObjects.current.monaco.editor.createModel(
            calculatedDiff.new === FILE_DOES_NOT_EXIST
              ? ""
              : calculatedDiff.new,
            getEditorLanguageForFilename(file?.filename)
          );

          // Calculated diffs can't be edited by default because the new contents
          // are not reflecting the current state of the codebase.
          // Therefore, only attach the changes listener if it has a callback
          if (file.onFileContentsChanged) {
            newModel.onDidChangeContent((_) => {
              const newContents = newModel.getValue();
              // @ts-expect-error TS2722
              file.onFileContentsChanged(newContents);
            });
          } else {
            newModel.onDidChangeContent(() => {});
          }

          monacoModelForFile = {
            original: originalModel,
            modified: newModel,
          };

          if (monacoModelForFile) {
            setFileToMonacoModel((prev) => ({
              ...prev,
              [fileKey]: monacoModelForFile,
            }));
          }
        } else {
          const oldContents =
            calculatedDiff.old === FILE_DOES_NOT_EXIST
              ? ""
              : calculatedDiff.old;

          const originalModel = (monacoModelForFile as editor.IDiffEditorModel)
            ?.original;
          const prevOldValue = originalModel?.getValue();

          if (prevOldValue !== oldContents) {
            throttledPushEditOperation(originalModel, oldContents);
          }

          const newContents =
            calculatedDiff.new === FILE_DOES_NOT_EXIST
              ? ""
              : calculatedDiff.new;

          const modifiedModel = (monacoModelForFile as editor.IDiffEditorModel)
            ?.modified;

          // Calculated diffs can't be edited by default because the new contents
          // are not reflecting the current state of the codebase.
          // Therefore, only attach the changes listener if it has a callback
          if (file.onFileContentsChanged) {
            modifiedModel.onDidChangeContent((_) => {
              const newContents = modifiedModel.getValue();
              // @ts-expect-error TS2722
              file.onFileContentsChanged(newContents);
            });
          } else {
            modifiedModel.onDidChangeContent(() => {});
          }

          const prevNewValue = modifiedModel?.getValue();

          if (prevNewValue !== newContents) {
            if (newContents?.startsWith(prevNewValue || "")) {
              const textAdded = newContents.slice(prevNewValue?.length || 0);

              const currentFullRange = modifiedModel?.getFullModelRange();
              // @ts-expect-error TS18047
              const insertRange = new monacoObjects.current.monaco.Range(
                currentFullRange.endLineNumber,
                currentFullRange.endColumn,
                currentFullRange.endLineNumber,
                currentFullRange.endColumn
              );

              modifiedModel.pushEditOperations(
                [],
                [
                  {
                    range: insertRange,
                    text: textAdded,
                    forceMoveMarkers: true,
                  },
                ],
                () => null
              );
            } else {
              throttledPushEditOperation(modifiedModel, newContents);
            }
          }
        }

        return;
      }

      // All other values rely on the value from the API
      const fileContentsFromApi = fileToContentsFromApi[file.filename];

      if (
        !file ||
        fileContentsFromApi === null ||
        fileContentsFromApi === undefined
      ) {
        return;
      }

      // First check suggested diff -> it relies on the value from the API
      // but has a special case for the new value
      if (fileType === FileType.SuggestedDiff) {
        if (!monacoDiffObjects.current?.monaco) {
          return;
        }

        let originalFileContentsToSet = fileContentsFromApi;
        if (
          file.hideOriginalCode ||
          originalFileContentsToSet === FILE_DOES_NOT_EXIST
        ) {
          originalFileContentsToSet = "";
        }

        if (!monacoModelForFile) {
          // Model has not been created yet, create it
          const originalModel =
            monacoDiffObjects.current?.monaco.editor.createModel(
              originalFileContentsToSet,
              getEditorLanguageForFilename(file?.filename)
            );
          const newModel = monacoDiffObjects.current?.monaco.editor.createModel(
            // @ts-expect-error TS2345
            file.suggestedDiff === FILE_DOES_NOT_EXIST
              ? ""
              : file.suggestedDiff,
            getEditorLanguageForFilename(file?.filename)
          );

          // If there is a callback, do not manage the state internally
          if (file.onFileContentsChanged) {
            newModel.onDidChangeContent((_) => {
              const newContents = newModel.getValue();
              // @ts-expect-error TS2722
              file.onFileContentsChanged(newContents);
            });
          } else {
            attachChangesListener(newModel, file, fileType);
          }

          monacoModelForFile = {
            original: originalModel,
            modified: newModel,
          };

          if (monacoModelForFile) {
            setFileToMonacoModel((prev) => ({
              ...prev,
              [fileKey]: monacoModelForFile,
            }));
          }
        } else {
          // The original value can only be updated by the API as it is the current file contents
          const prevValue = (
            monacoModelForFile as editor.IDiffEditorModel
          )?.original?.getValue();

          if (prevValue !== originalFileContentsToSet) {
            (monacoModelForFile as editor.IDiffEditorModel)?.original?.setValue(
              originalFileContentsToSet || ""
            );
          }

          const newContents =
            file.suggestedDiff === FILE_DOES_NOT_EXIST
              ? ""
              : file.suggestedDiff;

          const modifiedModel = (monacoModelForFile as editor.IDiffEditorModel)
            ?.modified;

          const prevNewValue = modifiedModel?.getValue();

          if (prevNewValue !== newContents) {
            if (newContents?.startsWith(prevNewValue || "")) {
              const textAdded = newContents.slice(prevNewValue?.length || 0);

              const currentFullRange = modifiedModel?.getFullModelRange();
              // @ts-expect-error TS18047
              const insertRange = new monacoObjects.current.monaco.Range(
                currentFullRange.endLineNumber,
                currentFullRange.endColumn,
                currentFullRange.endLineNumber,
                currentFullRange.endColumn
              );

              modifiedModel.pushEditOperations(
                [],
                [
                  {
                    range: insertRange,
                    text: textAdded,
                    forceMoveMarkers: true,
                  },
                ],
                () => null
              );
            } else {
              // @ts-expect-error TS2769
              throttledPushEditOperation(modifiedModel, newContents);
            }
          }
        }

        return;
      }

      if (fileType === FileType.Text) {
        if (!monacoObjects.current?.monaco) {
          return;
        }

        if (!monacoModelForFile) {
          // Model has not been created yet, create it
          monacoModelForFile = monacoObjects.current?.monaco.editor.createModel(
            fileContentsFromApi === FILE_DOES_NOT_EXIST
              ? ""
              : fileContentsFromApi,
            getEditorLanguageForFilename(file?.filename)
          );

          // Track unsaved changes on the model itself
          attachChangesListener(monacoModelForFile, file, fileType);

          if (monacoModelForFile) {
            setFileToMonacoModel((prev) => ({
              ...prev,
              [fileKey]: monacoModelForFile,
            }));
          }
        }
      } else if (fileType === FileType.Diff) {
        if (!monacoDiffObjects.current?.monaco) {
          return;
        }

        const fileContentsBeforeChanges = fileToContentsFromApi[fileKey];
        if (
          fileContentsBeforeChanges === null ||
          fileContentsBeforeChanges === undefined
        ) {
          return;
        }

        if (!monacoModelForFile) {
          // Model has not been created yet, create it
          const originalModel =
            monacoDiffObjects.current?.monaco.editor.createModel(
              fileContentsBeforeChanges === FILE_DOES_NOT_EXIST
                ? ""
                : fileContentsBeforeChanges,
              getEditorLanguageForFilename(file?.filename)
            );
          const newModel = monacoDiffObjects.current?.monaco.editor.createModel(
            fileContentsFromApi === FILE_DOES_NOT_EXIST
              ? ""
              : fileContentsFromApi,
            getEditorLanguageForFilename(file?.filename)
          );

          attachChangesListener(newModel, file, fileType);

          monacoModelForFile = {
            original: originalModel,
            modified: newModel,
          };

          if (monacoModelForFile) {
            setFileToMonacoModel((prev) => ({
              ...prev,
              [fileKey]: monacoModelForFile,
            }));
          }
        } else {
          // Otherwise update the original value, as it cannot be changed by the user
          const prevValue = (
            monacoModelForFile as editor.IDiffEditorModel
          )?.original?.getValue();

          if (prevValue !== fileContentsBeforeChanges) {
            (monacoModelForFile as editor.IDiffEditorModel)?.original?.setValue(
              fileContentsBeforeChanges || ""
            );
          }
        }
      }

      const hasUnsavedChanges = fileToHasUnsavedChanges[fileKey];

      if (!hasUnsavedChanges) {
        let monacoModelToChangeValue = file.diffType
          ? (monacoModelForFile as editor.IDiffEditorModel).modified
          : (monacoModelForFile as editor.ITextModel);

        let valueToSet = null;

        if (fileContentsFromApi === FILE_DOES_NOT_EXIST) {
          // @ts-expect-error TS2322
          valueToSet = "";
        } else {
          // @ts-expect-error TS2322
          valueToSet = fileContentsFromApi;
        }

        const prevValue = monacoModelToChangeValue?.getValue?.();

        if (
          monacoModelToChangeValue &&
          prevValue !== undefined &&
          prevValue !== valueToSet
        ) {
          const range = monacoModelToChangeValue?.getFullModelRange();
          if (range) {
            monacoModelToChangeValue.pushEditOperations(
              [],
              [
                {
                  range: range,
                  text: valueToSet,
                  forceMoveMarkers: true,
                },
              ],
              () => null
            );
          }
        }
      }
    };

    for (let file of editorFiles) {
      try {
        updateOrCreateModelForFile(file);
      } catch (e) {
        console.error(e);
      }
    }
  }, [
    editorFiles,
    fileToContentsFromApi,
    diffEditorLoaded,
    editorLoaded,
    selectedFileKey,
    calculatedDiffs,
  ]);

  /**
   * When the selected file changes, pick that model
   */
  useEffect(() => {
    if (!selectedFile) {
      return;
    }

    // @ts-expect-error TS2538
    const monacoModelForFile = fileToMonacoModel[selectedFileKey];

    if (!monacoModelForFile) {
      return;
    }

    if (prevOpenedFile) {
      const previousSelectedFile = getKeyForFile(prevOpenedFile);

      if (diffEditorForFile(prevOpenedFile)) {
        const viewState = monacoDiffObjects.current?.editor?.saveViewState();
        // @ts-expect-error TS2345
        setFileToViewState((prev) => {
          return {
            ...prev,
            [previousSelectedFile]: viewState,
          };
        });
      } else {
        const viewState = monacoObjects.current?.editor?.saveViewState();
        // @ts-expect-error TS2345
        setFileToViewState((prev) => {
          return {
            ...prev,
            [previousSelectedFile]: viewState,
          };
        });
      }
    }

    let currentValueInModel;
    if (selectedFileIsDiff) {
      const diffModel = monacoModelForFile as editor.IDiffEditorModel;
      if (!diffModel.original || !diffModel.modified) {
        return;
      }

      monacoDiffObjects.current?.editor?.setModel(diffModel);
      currentValueInModel = diffModel?.modified?.getValue();

      if (
        selectedFile.calculatedDiff ||
        (selectedFile.suggestedDiff !== undefined &&
          selectedFile.suggestedDiff !== null)
      ) {
        // Calculated diffs can't be edited by default because the new contents
        // are not reflecting the current state of the codebase.
        // Therefore, only allow changes if it has a callback
        const readOnly =
          !selectedFile.onFileContentsChanged &&
          (selectedFile.suggestedDiff === undefined ||
            selectedFile.suggestedDiff === null);

        let readOnlyMessage;
        if (selectedFile.context === EditorContext.History) {
          if (
            fileToContentsFromApi?.[selectedFile.filename] ===
            FILE_DOES_NOT_EXIST
          ) {
            readOnlyMessage = "Cannot edit, file is now deleted";
          } else {
            readOnlyMessage =
              "Cannot edit, click on the top right to edit the current version";
          }
        }

        monacoDiffObjects.current?.editor.updateOptions({
          renderSideBySide: false,
          readOnly,
          readOnlyMessage:
            readOnlyMessage && readOnly
              ? {
                  value: readOnlyMessage,
                  isTrusted: true,
                }
              : undefined,
        });
      } else {
        monacoDiffObjects.current?.editor.updateOptions({
          renderSideBySide: true,
          readOnly: false,
        });
      }
    } else {
      if (
        (monacoModelForFile as editor.IDiffEditorModel).modified ||
        (monacoModelForFile as editor.IDiffEditorModel).original
      ) {
        return;
      }

      monacoObjects.current?.editor?.setModel(
        monacoModelForFile as editor.ITextModel
      );
      currentValueInModel = (
        monacoModelForFile as editor.ITextModel
      )?.getValue();
    }

    // @ts-expect-error TS2538
    if (fileToViewState[selectedFileKey]) {
      if (selectedFileIsDiff) {
        monacoDiffObjects.current?.editor?.restoreViewState(
          // @ts-expect-error TS2538
          fileToViewState[selectedFileKey] as editor.IDiffEditorViewState
        );
      } else {
        monacoObjects.current?.editor?.restoreViewState(
          // @ts-expect-error TS2538
          fileToViewState[selectedFileKey] as editor.ICodeEditorViewState
        );
      }
    }

    // @ts-expect-error TS2538
    const hasUnsavedChanges = fileToHasUnsavedChanges[selectedFileKey];
    const fileContentsOnDisk = fileToContentsFromApi[selectedFile.filename];

    // Create decorations for changes to the file based on the previous value
    // this highlights any changes made by other parts of our app in the code editor
    if (!hasUnsavedChanges && filesEqual(selectedFile, prevOpenedFile)) {
      createHighlightChanges(currentValueInModel, fileContentsOnDisk);
    }
  }, [selectedFileKey, fileToMonacoModel]);

  const createHighlightChanges = (oldContents: string, newContents: string) => {
    if (oldContents === null || oldContents === undefined) {
      return;
    }

    if (newContents === null || newContents === undefined) {
      return;
    }

    if (oldContents === newContents) {
      return;
    }

    const oldLines = oldContents.split("\n");
    const newLines = newContents.split("\n");

    const newMonacoDecorations = [];

    if (newLines.length !== oldLines.length) {
      const oldLinesSet = new Set<string>(oldLines);
      for (let i = 0; i < newLines.length; i++) {
        const line = newLines[i];
        if (!oldLinesSet.has(line)) {
          // @ts-expect-error TS2345
          newMonacoDecorations.push({
            range: [i + 1, 1, i + 1, line.length],
            options: {
              isWholeLine: true,
              inlineClassName: "line-diff-decoration",
            },
          });
        }
      }
    } else {
      for (let i = 0; i < newLines.length; i++) {
        const line = newLines[i];
        const oldLine = oldLines[i];
        if (line !== oldLine) {
          for (let chr = 0; chr < line.length; chr += 1) {
            if (line[chr] !== oldLine[chr]) {
              // @ts-expect-error TS2345
              newMonacoDecorations.push({
                range: [i + 1, chr + 1, i + 1, line.length],
                options: {
                  inlineClassName: "line-diff-decoration",
                },
              });
              break;
            }
          }
        }
      }
    }

    setDecorationsHighlightChanges(newMonacoDecorations);
  };

  /**
   * Set the decorations on the editor after the editor value has changed
   */
  useEffect(() => {
    if (!editorFiles.length || !selectedFile || selectedFileIsDiff) {
      return;
    }

    const fileContentsOnDisk = fileToContentsFromApi[selectedFile.filename];
    if (!fileContentsOnDisk) {
      return;
    }

    if (!monacoObjects.current) return;

    const { editor, monaco } = monacoObjects.current;

    if (!editor || !monaco || !editor.deltaDecorations) {
      return;
    }

    if (decorationsHighlightChanges.length || selectedFile?.highlightLines) {
      let decorations = decorationsHighlightChanges.map((decoration) => {
        return {
          // @ts-ignore
          range: new monaco.Range(...decoration.range),
          options: decoration.options,
        };
      });

      const decorationIds = editor.deltaDecorations([], decorations);

      setTimeout(() => {
        editor.deltaDecorations(decorationIds, []);
        setDecorationsHighlightChanges([]);
      }, 500);

      if (selectedFile?.highlightLines) {
        let lineStart = selectedFile?.highlightLines.start;
        let lineEnd = selectedFile?.highlightLines.end;

        if (selectedFile?.highlightLines.charPos) {
          const before = fileContentsOnDisk.substring(
            0,
            selectedFile?.highlightLines.charPos
          );
          lineStart = before.split("\n").length;
          lineEnd = lineStart;
        }

        // Clear the highlightLines from redux
        clearHighlightLinesForFile(selectedFile);

        if (lineStart === undefined || lineEnd === undefined) {
          console.error(
            "Error jump to line: lineStart or lineEnd is undefined",
            lineStart,
            lineEnd
          );
          return;
        }

        // Provide some time for the editor to render
        setTimeout(() => {
          if (highlightLineDecoration) {
            editor.deltaDecorations([highlightLineDecoration], []);
          }

          setHighlightLineDecoration(
            // @ts-expect-error TS2345
            editor.deltaDecorations(
              [],
              [
                {
                  range: new monaco.Range(lineStart, 1, lineEnd, 1),
                  options: {
                    isWholeLine: true,
                    linesDecorationsClassName: "line-diff-decoration",
                  },
                },
              ]
            )
          );

          editor.revealLineInCenter(lineStart);
          notifyJumpToLine(selectedFile.filename, lineStart);
        }, 100);
      }
    }
  }, [
    editorFiles,
    selectedFile,
    fileToContentsFromApi,
    diffEditorLoaded,
    editorLoaded,
  ]);

  const handleDiffEditorMount = (
    editor: editor.IStandaloneDiffEditor,
    monaco: MonacoType
  ) => {
    // @ts-expect-error TS2540
    monacoDiffObjects.current = {
      editor,
      monaco,
    };
    setDiffEditorLoaded(true);
  };

  const handleEditorMount = (
    editor: editor.IStandaloneCodeEditor,
    monaco: MonacoType
  ) => {
    // @ts-expect-error TS2540
    monacoObjects.current = {
      editor,
      monaco,
    };
    setEditorLoaded(true);
  };

  // Create the editors on mount once and not again, so that the models are never cleared
  const options: editor.IStandaloneDiffEditorConstructionOptions = {
    minimap: { enabled: false },
    overviewRulerLanes: 0,

    scrollbar: {
      vertical: "hidden",
      horizontal: "hidden",
      handleMouseWheel: true,
    },

    // Undocumented see https://github.com/Microsoft/vscode/issues/30795#issuecomment-410998882
    lineDecorationsWidth: 0,
    lineNumbersMinChars: 0,
    scrollBeyondLastLine: false,
    automaticLayout: true,
    wordWrap: "on",
    fontSize: 11,

    padding: {
      top: 4,
    },
  };

  const addExample = useDesignSystemStore((state) => state.addExample);
  const setChatContext = useAiContextStore((state) => state.setChatContext);

  useEffect(() => {
    if (!monacoObjects.current) return;
    if (!monacoObjects.current.editor) return;

    const { monaco: nonDiffMonaco, editor: nonDiffEditor } =
      monacoObjects.current || {};
    const { monaco: diffMonaco, editor: diffEditor } =
      monacoDiffObjects.current || {};

    const addActionsToEditor = (
      monaco: MonacoType,
      editor: editor.IStandaloneCodeEditor,
      originalEditor?: editor.IStandaloneCodeEditor
    ) => {
      if (!editor || !monaco) {
        return;
      }

      if (
        editor.getAction("add-selection-as-example") ||
        editor.getAction("add-selection-chat-context")
      ) {
        return;
      }

      editor.addAction({
        id: "add-selection-as-example",
        label: "Add to Story Builder",
        keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyB],
        contextMenuGroupId: "navigation",
        contextMenuOrder: 1,
        run: (ed) => {
          // @ts-expect-error TS2531
          const selection = ed.getModel().getValueInRange(ed.getSelection());
          addExample(selection);
          toast.info("Added selection as example!");
        },
      });

      editor.addAction({
        id: "add-selection-chat-context",
        label: "Add to Chat",
        keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyL],
        contextMenuGroupId: "navigation",
        contextMenuOrder: 1,
        run: (ed) => {
          try {
            // Not supported for AI chat
            // TODO: Figure out how to remove instead of showing a warning
            // See: https://github.com/microsoft/monaco-editor/issues/1567
            if (
              Boolean(selectedFileRef.current.calculatedDiff) ||
              Boolean(selectedFileRef.current.diffType) ||
              selectedFileRef.current.rawContents ||
              selectedFileRef.current.rawContents === ""
            ) {
              toast.warning("Not yet supported");
              return;
            }

            const range = ed.getSelection();

            // @ts-expect-error TS2531
            const selection = ed.getModel().getValueInRange(range);
            const selectedFile = selectedFileRef.current;

            const currentEditorContext =
              useCodebaseStore.getState().editorContext;

            const codeSnippet: ChatContextCodeSnippet = {
              filename: selectedFile.filename,
              // @ts-expect-error TS18047
              startLine: range.startLineNumber,
              // @ts-expect-error TS18047
              startCol: range.startColumn,
              // @ts-expect-error TS18047
              endLine: range.endLineNumber,
              // @ts-expect-error TS18047
              endCol: range.endColumn,
              contents: selection,
              editorContext: currentEditorContext,
            };

            if (
              openedCommitFileRef.current &&
              selectedFileRef.current.context === EditorContext.EngSpec
            ) {
              codeSnippet.engSpecFileDiffId = openedCommitFileRef.current?.id;
              codeSnippet.engSpecCommitId = selectedCommitRef.current?.id;
            }

            let diffCodeSnippet;
            if (originalEditor) {
              // @ts-expect-error TS2531
              const diffSelection = originalEditor
                .getModel()
                // @ts-expect-error TS2345
                .getValueInRange(range);

              diffCodeSnippet = {
                ...codeSnippet,
                contents: diffSelection,
              };
            }

            if (
              leftTabRef.current !== LeftTab.AIChat &&
              leftTabRef.current !== LeftTab.EngSpec
            ) {
              setNewLayout(LeftTab.AIChat);
            }

            setChatContext(codeSnippet, diffCodeSnippet);
          } catch (e) {
            toast.error("Error adding code snippet");
            console.error(e);
          }
        },
      });
    };

    addActionsToEditor(nonDiffMonaco, nonDiffEditor);
    addActionsToEditor(
      // @ts-expect-error TS2345
      diffMonaco,
      diffEditor?.getModifiedEditor?.(),
      diffEditor?.getOriginalEditor?.()
    );
  }, [monacoObjects.current, selectedFile]);

  const editor = useMemo(() => {
    return (
      <Editor
        key="non-diff-editor"
        height={"100%"}
        width={"100%"}
        options={options}
        onMount={(editor, monaco) => {
          initEditor(monaco);

          monaco.editor.setTheme("tempo-theme");

          handleEditorMount(editor, monaco);
        }}
        defaultLanguage="javascript"
      />
    );
  }, []);

  const diffEditor = useMemo(() => {
    return (
      <DiffEditor
        key="diff-editor"
        options={{
          minimap: { enabled: false },
          overviewRulerLanes: 0,

          scrollbar: {
            vertical: "hidden",
            horizontal: "hidden",
            handleMouseWheel: true,
          },

          // Undocumented see https://github.com/Microsoft/vscode/issues/30795#issuecomment-410998882
          lineDecorationsWidth: 0,
          lineNumbersMinChars: 0,
          scrollBeyondLastLine: false,
          automaticLayout: true,
          wordWrap: "on",
          fontSize: 11,

          padding: {
            top: 4,
          },
          readOnly: false,
        }}
        theme={"vs-dark"}
        onMount={(editor, monaco) => {
          initEditor(monaco);

          monaco.editor.setTheme("tempo-theme");

          handleDiffEditorMount(editor, monaco);
        }}
      />
    );
  }, []);

  const handleTabClick = useCallback((file) => {
    setSelectedFile(file);
  }, []);

  const handleTabDoubleClick = useCallback((file) => {
    hardOpenFile(file);
  }, []);

  const handleTabCloseClick = useCallback((file) => {
    const editorValueKey = getKeyForFile(file);

    closeFile(file);

    // Clear the editor values for that filename
    setFileToMonacoModel((prev) => {
      const newFileToMonacoModel = { ...prev };
      const modelToDispose = newFileToMonacoModel[editorValueKey];

      // Properly dispose Monaco models before deletion
      if (modelToDispose) {
        try {
          if ((modelToDispose as editor.IDiffEditorModel).original) {
            // Reset diff editor first before disposing models
            if (selectedFileIsDiff && filesEqual(selectedFile, file)) {
              monacoDiffObjects.current?.editor?.setModel(null);
            }

            // It's a diff editor model - dispose both original and modified
            const diffModel = modelToDispose as editor.IDiffEditorModel;
            diffModel.original?.dispose();
            diffModel.modified?.dispose();
          } else {
            // Reset regular editor first before disposing model
            if (!selectedFileIsDiff && filesEqual(selectedFile, file)) {
              monacoObjects.current?.editor?.setModel(null);
            }

            // It's a regular text model
            (modelToDispose as editor.ITextModel)?.dispose();
          }
        } catch (e) {
          console.error("Error disposing Monaco model:", e);
        }
      }

      delete newFileToMonacoModel[editorValueKey];
      return newFileToMonacoModel;
    });
    setFileToHasUnsavedChanges((prev) => {
      const newPrev = { ...prev };
      delete newPrev[editorValueKey];
      return newPrev;
    });
  }, []);

  const handleTabSave = useCallback(
    (file) => {
      const fileKey = getKeyForFile(file);

      const monacoModel = fileToMonacoModel[fileKey];

      if (fileToHasUnsavedChanges[fileKey] && monacoModel) {
        const monacoModelWithChanges = file.diffType
          ? (monacoModel as editor.IDiffEditorModel).modified
          : (monacoModel as editor.ITextModel);

        if (!monacoModelWithChanges) {
          return;
        }

        const newContents = monacoModelWithChanges.getValue();

        track("FILE_SAVE", {
          from: "BottomPanel",
        });

        // Clear the file to unsaved changes immediately so that it shows the update
        setFileToHasUnsavedChanges((prev) => {
          return {
            ...prev,
            [fileKey]: false,
          };
        });

        saveFile({
          fileToContentsToSave: { [file.filename]: newContents },
          filenameToFileKey: { [file.filename]: fileKey },
          saveActivityType: file.saveActivityType,
        }).catch((err) => {
          // If it fails clear the change in the fileToHasUnsavedChanges
          setFileToHasUnsavedChanges((prev) => {
            return {
              ...prev,
              [fileKey]: true,
            };
          });
        });
      }
    },
    [
      fileToHasUnsavedChanges,
      Boolean(session),
      canvasId,
      fileToMonacoModel,
      saveFile,
    ]
  );

  const getTabs = useDeepMemo(() => {
    return editorFiles.map((file: EditorFile, index: number) => {
      const showSave = fileToHasUnsavedChanges[getKeyForFile(file)];

      return (
        <Tab
          key={file.filename + (selectedFileIsDiff ? "diff" : "") + index}
          file={file}
          selected={filesEqual(selectedFile, file)}
          onTabClick={handleTabClick}
          onTabDoubleClick={handleTabDoubleClick}
          onTabSave={handleTabSave}
          onTabCloseClick={handleTabCloseClick}
          showSave={showSave}
          selectedFile={selectedFile}
        />
      );
    });
  }, [
    editorFiles,
    selectedFile,
    fileToHasUnsavedChanges,
    fileToContentsFromApi,
    selectedFileIsDiff,
  ]);

  // Function to handle save on Cmd + S or Ctrl + S
  const handleSaveShortcut = useCallback(
    (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "s") {
        event.preventDefault();
        if (monacoObjects.current?.editor.hasTextFocus()) {
          // Assuming chosenFile represents the current file being edited

          monacoObjects.current?.editor?.getDomNode()?.blur();
          handleTabSave(selectedFile);
        } else if (monacoDiffObjects.current?.editor.hasTextFocus()) {
          // Assuming chosenFile represents the current file being edited

          handleTabSave(selectedFile);
        }
      }
    },
    [editorLoaded, diffEditorLoaded, selectedFile, handleTabSave]
  );

  // Attach the event listener
  useEffect(() => {
    window.addEventListener("keydown", handleSaveShortcut);

    // Cleanup the event listener
    return () => {
      window.removeEventListener("keydown", handleSaveShortcut);
    };
  }, [handleSaveShortcut]);
  // Attach the event listener
  useEffect(() => {
    window.addEventListener("keydown", handleSaveShortcut);

    // Cleanup the event listener
    return () => {
      window.removeEventListener("keydown", handleSaveShortcut);
    };
  }, []);

  // Cleanup Monaco models on component unmount
  useEffect(() => {
    return () => {
      // Reset editors first before disposing models
      try {
        monacoObjects.current?.editor?.setModel(null);
        monacoDiffObjects.current?.editor?.setModel(null);
      } catch (e) {
        console.error("Error resetting editors on unmount:", e);
      }

      // Dispose all Monaco models when component unmounts
      Object.values(fileToMonacoModel).forEach((model) => {
        try {
          if ((model as editor.IDiffEditorModel).original) {
            const diffModel = model as editor.IDiffEditorModel;
            diffModel.original?.dispose();
            diffModel.modified?.dispose();
          } else {
            (model as editor.ITextModel)?.dispose();
          }
        } catch (e) {
          console.error("Error disposing Monaco model on unmount:", e);
        }
      });
    };
  }, []);

  // Actions at the top of the file
  const showEngSpecActions = engSpecMode && engSpecOpenedFile?.change;
  const showUndoEngSpec =
    showEngSpecActions &&
    latestActivity?.id &&
    engSpecOpenedFile.executedActivityId === latestActivity?.id;
  const showRedoEngSpec =
    showEngSpecActions &&
    nextActivityToRedo?.id &&
    engSpecOpenedFile.executedActivityId === nextActivityToRedo?.id;

  const appliedDiffActivity = activityStreamList?.find?.(
    (activity) =>
      selectedFile?.appliedDiffActivityId === activity.id &&
      !activity.change_undone
  );

  // For suggested diffs
  const showAcceptDiscardSuggestedDiff =
    selectedFile?.suggestedDiff !== undefined &&
    selectedFile?.suggestedDiff !== null &&
    selectedFile?.suggestedDiff !==
      fileToContentsFromApi[selectedFile.filename] &&
    !showEngSpecActions &&
    !appliedDiffActivity;
  const showUndoSuggestedDiff =
    appliedDiffActivity &&
    appliedDiffActivity.id === latestActivity?.id &&
    !showEngSpecActions;

  const showAcceptDiscard = showAcceptDiscardSuggestedDiff;
  const showUndo = showUndoEngSpec || showUndoSuggestedDiff;
  const showRedo = showRedoEngSpec;

  return (
    <div
      className={cn(
        "w-full h-full bottom-0 relative flex flex-col border-l border-r bg-secondary-100"
      )}
    >
      <div className={cn(`flex grow flex-col basis-0 justify-end`)}>
        <div
          className={cn(
            "flex flex-row justify-center items-center bg-secondary-200",
            engSpecMode && "hidden"
          )}
        >
          <div className="flex flex-row overflow-x-scroll hide-scrollbar grow">
            <div
              className={cn(
                "flex flex-row justify-start items-center grow h-8 border-b"
              )}
            >
              {getTabs}
            </div>
          </div>
          <div
            onClick={() => {
              // Close both the code editor and the "Change made from chat" panel
              closeCodeDiffPanel();
              setNewLayout(LeftTab.AIChat);
            }}
          >
            <X className="w-4 h-4 cursor-pointer text-secondary-800 hover:text-secondary-900 mr-1" />
          </div>
        </div>

        <div
          className={cn(
            "flex space-x-2 items-center mt-1 mx-2 rounded-md p-0 p-1 justify-center bg-secondary-200 justify-between",
            !showAcceptDiscard && !showUndo && !showRedo && "hidden"
          )}
        >
          <div
            className={cn(
              "flex flex-row items-center gap-1 text-xs pl-2",
              !showEngSpecActions && "invisible"
            )}
          >
            Files Read: {engSpecOpenedCommit?.filenamesReadForContext?.length}
            <InfoIcon
              className="w-3 h-3 cursor-pointer"
              data-tooltip-id="tooltip"
              data-tooltip-place="bottom"
              data-tooltip-html={engSpecOpenedCommit?.filenamesReadForContext
                ?.map((filename: string) => {
                  return `<div class="text-xs">${filename}</div>`;
                })
                .join("")}
            />
          </div>

          <div className="flex flex-row items-center gap-2">
            {showAcceptDiscard ? (
              <>
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={() => {
                    if (showEngSpecActions) {
                      return;
                    }

                    // Otherwise - it's the suggested diff that can be closed
                    updateFile(selectedFile, {
                      // @ts-expect-error TS2322
                      suggestedDiff: null,
                    });
                  }}
                >
                  Discard
                </Button>

                <Button
                  variant="secondary"
                  className="bg-primary-0 hover:bg-[#4e45be] text-white"
                  size="xs"
                  onClick={() => {
                    if (showEngSpecActions || !selectedFile?.suggestedDiff) {
                      return;
                    }

                    // Otherwise - it's the suggested diff that can be saved
                    saveFile({
                      fileToContentsToSave: {
                        [selectedFile.filename]: selectedFile.suggestedDiff,
                      },
                      filenameToFileKey: {
                        [selectedFile.filename]: getKeyForFile(selectedFile),
                      },
                      saveActivityType: selectedFile.saveActivityType,
                    }).then((res) => {
                      if (res.data?.activityId) {
                        updateFile(selectedFile, {
                          appliedDiffActivityId: res.data.activityId,
                        });
                      }
                    });
                  }}
                >
                  Accept
                </Button>
              </>
            ) : null}

            {showUndo || showRedo ? (
              <>
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={async () => {
                    if (showRedo) {
                      redoChange();
                    } else {
                      undoChange();
                    }
                  }}
                >
                  {showRedo ? "Redo" : "Undo"}
                </Button>
                {showUndoEngSpec &&
                  Boolean(filenameToComponent[selectedFile?.filename]) && (
                    <Button
                      variant="primary"
                      size="xs"
                      className="flex flex-row gap-1"
                      onClick={() => {
                        setCanvasComponentKey(
                          filenameToComponent[selectedFile.filename].key
                        );
                        // @ts-expect-error TS2345
                        setEngSpecOpenedFile(null, false);
                      }}
                    >
                      <EyeIcon className="w-4 h-4" />
                      View
                    </Button>
                  )}
              </>
            ) : null}
          </div>
        </div>

        <div
          className={cn("flex flex-col grow")}
          style={{
            opacity: editorFiles.length ? 1 : 0,
          }}
          ref={editorWrapperRef}
        >
          <div
            className={cn("flex h-full w-full", selectedFileIsDiff && "hidden")}
          >
            {editor}
          </div>
          <div
            className={cn(
              "flex h-full w-full relative",
              !selectedFileIsDiff && "hidden"
            )}
          >
            {(selectedFile?.context === EditorContext.History ||
              (selectedFile?.context === EditorContext.Code &&
                selectedFile?.diffType)) && (
              <Button
                className="absolute right-1 top-1 flex items-center justify-center gap-2 px-2 h-7 z-[1000] rounded-none opacity-90 hover:opacity-100 transition transition-all border-primary-0 border"
                size="sm"
                variant="outline"
                disabled={
                  fileToContentsFromApi?.[selectedFile.filename] ===
                  FILE_DOES_NOT_EXIST
                }
                onClick={() => {
                  openFile({
                    file: {
                      filename: selectedFile.filename,
                      context: EditorContext.Code,
                    },
                    jumpToTab: true,
                  });
                  // @ts-expect-error TS2345
                  setNewLayout(LeftTab.FileSelector, null);
                }}
              >
                {fileToContentsFromApi?.[selectedFile.filename] ===
                FILE_DOES_NOT_EXIST ? (
                  "File Deleted"
                ) : (
                  <>
                    <PencilIcon className="w-3 h-3" />
                    {selectedFile?.context === EditorContext.History
                      ? "Edit File"
                      : "View Current File"}
                  </>
                )}
              </Button>
            )}
            {diffEditor}
          </div>
        </div>
      </div>
    </div>
  );
});

export default CodePanel;
